// Enums
import { ChartOptions, ChartStyle, ChartType, Frequency, TechChartAnnotationName, TechChartDateSelection, TechChartScaleType, TechChartSecurityKey, TechnicalKey, TechnicalType, } from 'ChartApp/ts/enums/tech-chart-common.enum';
import { ChartDateZoom, NameInLegendOption } from 'ChartApp/ts/enums/chart-common.enum';
// sharing urls
export const CHART_BASE_SHARING_URL_OPTIONS = {
    TWITTER: {
        url: '/charts/share_twitter/auth/tech/',
        width: 720,
        height: 780,
    },
};
// Constants
import { COLOR_PICKER_OPTIONS_DEFAULT } from 'NewAngular/features/components/inputs/color-picker/color-picker.constant';
import { COPY_URL_LINK, EMBED, SHARE, TWEET } from 'NewAngular/constants/core.constants';
// default security color
export const DEFAULT_SECURITY_COLOR = '#6833c9';
// default partner option
export const TEACH_CHART_PARTNER_OPTION_DEFAULT = 'basic_850';
// default tech chart technicals
export const TECH_CHART_TECHNICALS_DEFAULT = [
    // Location indicator (oscillator)
    { type: TechnicalType.VOLUME, params: [], include: true, color: '#1A1A1A' },
    {
        type: TechnicalType.RSI,
        params: [4, 3, 14],
        include: true,
        color: COLOR_PICKER_OPTIONS_DEFAULT[1].toUpperCase(),
    },
    // Location overlay
    // The first default color is reserved for the OHLC data
    { type: TechnicalType.SMA, params: [3, 50], include: true, color: COLOR_PICKER_OPTIONS_DEFAULT[1].toUpperCase() },
    { type: TechnicalType.SMA, params: [3, 200], include: true, color: COLOR_PICKER_OPTIONS_DEFAULT[2].toUpperCase() },
];
// technicals with nested array type
export const TECH_CHART_NESTED_ARRAY_TYPES = [
    TechnicalType.STOCHASTIC_FULL,
    TechnicalType.STOCHASTIC_SLOW,
];
// chart type constants
export const TECH_CHART_TYPE_OPTIONS = [ChartType.INTERACTIVE, ChartType.PRESENTATION];
export const TECH_CHART_TYPE_DEFAULT = TECH_CHART_TYPE_OPTIONS[0];
// valid zoom options
export const VALID_TECH_CHART_DATE_ZOOM_OPTIONS = [
    { label: '1d', value: ChartDateZoom.CHART_ZOOM_ONE_DAY },
    { label: '5d', value: ChartDateZoom.CHART_ZOOM_FIVE_DAY },
    { label: '1m', value: ChartDateZoom.CHART_ZOOM_ONE_MONTH },
    { label: '3m', value: ChartDateZoom.CHART_ZOOM_THREE_MONTH },
    { label: '6m', value: ChartDateZoom.CHART_ZOOM_SIX_MONTH },
    { label: 'YTD', value: ChartDateZoom.CHART_ZOOM_YTD },
    { label: '1y', value: ChartDateZoom.CHART_ZOOM_ONE_YEAR },
    { label: '3y', value: ChartDateZoom.CHART_ZOOM_THREE_YEAR },
    { label: '5y', value: ChartDateZoom.CHART_ZOOM_FIVE_YEAR },
    { label: '10y', value: ChartDateZoom.CHART_ZOOM_TEN_YEAR },
    { label: 'Max', value: ChartDateZoom.CHART_ZOOM_MAX },
];
export const TECH_CHART_INTRADAY_DATE_FORMAT = 'MMM d yy HH:mm';
export const TECH_CHART_MULTI_DAY_DATE_FORMAT = 'MMM d yy';
export const TECH_CHART_OPTIONS = [
    {
        label: 'Dividend Adjusted',
        value: ChartOptions.ADJUSTED,
        presentationOnly: false,
    },
    {
        label: 'Show US Recessions',
        value: ChartOptions.SHOW_US_RECESSIONS,
        presentationOnly: false,
        helpName: 'show_us_recessions',
    },
    {
        label: 'Log Scale',
        value: ChartOptions.USE_LOG_SCALE,
        trueValue: 'log',
        falseValue: 'linear',
        presentationOnly: false,
    },
    {
        label: 'Show Legend on Chart',
        value: ChartOptions.LEGEND_ON_CHART,
        presentationOnly: true,
    },
    {
        label: 'Display Units',
        value: ChartOptions.SHOW_UNITS_IN_LEGEND,
        presentationOnly: false,
    },
    // {
    //     label: 'Quote Style Legend',
    //     value: ChartOptions.QUOTE_LEGEND,
    //     presentationOnly: true,
    // },
    {
        label: 'Show Date Range',
        value: ChartOptions.DISPLAY_DATE_RANGE,
        presentationOnly: true,
    },
    {
        label: 'Show Performance Disclosure',
        value: ChartOptions.SHOW_PERFORMANCE_DISCLOSURE,
        presentationOnly: true,
    },
    {
        label: 'Hide Zoom Bar',
        value: ChartOptions.HIDE_ZOOM_BAR,
        presentationOnly: false,
    },
];
// Chart scale types
export const SCALE_TYPE_DEFAULT = TechChartScaleType.LINEAR;
// default chart style
export const TECH_CHART_STYLE_DEFAULT = ChartStyle.CANDLESTICK;
// default name in legend options
export const TECH_CHART_NAME_IN_LEGEND_DEFAULT = NameInLegendOption.nameAndTicker;
// default frequency
export const TECH_CHART_FREQUENCY_DEFAULT = Frequency.DAILY;
// default date selection
export const TECH_CHART_DATE_SELECTION_DEFAULT = TechChartDateSelection.RANGE;
export const TECH_CHART_APP_STATE_SLICE_KEY = 'techChart';
// Keys in state that are used to create a saved tech chart.
// Note that there is no 'chartId' value yet; that can exist after a saved tech chart is created.
export const SAVED_TECH_CHART_VIEW_STATE_KEYS = [
    'chartAnnotations',
    'chartName',
    ChartOptions.ADJUSTED,
    ChartOptions.SHOW_US_RECESSIONS,
    ChartOptions.USE_LOG_SCALE,
    ChartOptions.NAME_IN_LEGEND,
    ChartOptions.SHOW_UNITS_IN_LEGEND,
    ChartOptions.QUOTE_LEGEND,
    ChartOptions.LEGEND_ON_CHART,
    ChartOptions.DISPLAY_DATE_RANGE,
    ChartOptions.HIDE_ZOOM_BAR,
    'frequency',
    'chartType',
    'dateSelection',
    'endDate',
    'liveData',
    'note',
    'partner',
    'performanceDisclosure',
    'security',
    'securityGroup',
    'securitylistName',
    'securitylistSecurityId',
    'startDate',
    'style',
    'technicals',
    'title',
    'zoom',
];
// state
export const TECH_CHART_STATE_KEYS = [...SAVED_TECH_CHART_VIEW_STATE_KEYS, 'chartId'];
const TECH_CHART_DEFAULT_CHART_OPTIONS = {
    [ChartOptions.ADJUSTED]: false,
    [ChartOptions.SHOW_US_RECESSIONS]: false,
    [ChartOptions.USE_LOG_SCALE]: SCALE_TYPE_DEFAULT,
    [ChartOptions.NAME_IN_LEGEND]: TECH_CHART_NAME_IN_LEGEND_DEFAULT,
    [ChartOptions.SHOW_UNITS_IN_LEGEND]: false,
    [ChartOptions.QUOTE_LEGEND]: true,
    [ChartOptions.LEGEND_ON_CHART]: true,
    [ChartOptions.HIDE_ZOOM_BAR]: false,
    [ChartOptions.SHOW_PERFORMANCE_DISCLOSURE]: false,
};
export const TECH_CHART_SYSTEM_DEFAULT_STATE = {
    chartAnnotations: [],
    chartType: TECH_CHART_TYPE_DEFAULT,
    chartId: undefined,
    startDate: '',
    endDate: '',
    dateSelection: TechChartDateSelection.RANGE,
    frequency: Frequency.DAILY,
    security: null,
    securityGroup: '',
    securitylistName: '',
    securitylistSecurityId: '',
    style: TECH_CHART_STYLE_DEFAULT,
    technicals: TECH_CHART_TECHNICALS_DEFAULT,
    zoom: ChartDateZoom.CHART_ZOOM_ONE_YEAR,
    liveData: false,
    note: '',
    partner: TEACH_CHART_PARTNER_OPTION_DEFAULT,
    title: '',
    ...TECH_CHART_DEFAULT_CHART_OPTIONS,
};
export const TECHNICAL_KEYS = Object.values(TechnicalKey).sort();
export const TECH_CHART_SECURITY_KEYS = Object.values(TechChartSecurityKey);
export const SAVED_TECH_CHART_SHARING_OPTIONS = {
    title: {
        label: 'Share',
        icon: 'icon-share',
    },
    list: [
        {
            type: 'option',
            label: 'Share with Team',
            value: SHARE,
            icon: 'icon-team',
        },
        {
            type: 'divider',
            label: null,
            value: null,
            icon: null,
        },
        {
            type: 'option',
            label: 'Tweet',
            value: TWEET,
            icon: 'icon-tweet',
        },
        {
            type: 'option',
            label: 'Embed Code',
            value: EMBED,
            icon: 'icon-embed',
        },
        {
            type: 'option',
            label: 'Copy Link',
            value: COPY_URL_LINK,
            icon: 'icon-link',
        },
    ],
};
export const TECH_CHART_INDICATOR_MENU_CATEGORIES = [
    {
        cat_section: 'pro_metrics',
        category_label: 'Overlays',
        sections: [
            {
                items: [
                    {
                        internal_name: TechnicalType.BOLLINGER,
                        label: 'Bollinger Bands™',
                    },
                    {
                        internal_name: TechnicalType.CHANDELIER_EXIT_LONG,
                        label: 'Chandelier Exit (Long)',
                    },
                    {
                        internal_name: TechnicalType.CHANDELIER_EXIT_SHORT,
                        label: 'Chandelier Exit (Short)',
                    },
                    {
                        internal_name: TechnicalType.EMA,
                        label: 'Exponential Moving Average (EMA)',
                    },
                    {
                        internal_name: TechnicalType.ICHIMOKU,
                        label: 'Ichimoku Clouds',
                    },
                    {
                        internal_name: TechnicalType.KELTNER_CHANNELS,
                        label: 'Keltner Channels',
                    },
                    {
                        internal_name: TechnicalType.MA_ENVELOPES,
                        label: 'MA Envelopes',
                    },
                    {
                        internal_name: TechnicalType.SMA,
                        label: 'Simple Moving Average (SMA)',
                    },
                ],
                section_label: '',
            },
        ],
    },
    {
        cat_section: 'pro_metrics',
        category_label: 'Oscillators',
        sections: [
            {
                items: [
                    {
                        internal_name: TechnicalType.AROON,
                        label: 'Aroon',
                    },
                    {
                        internal_name: TechnicalType.AROON_OSCILLATOR,
                        label: 'Aroon Oscillator',
                    },
                    {
                        internal_name: TechnicalType.ATR,
                        label: 'Average True Range (ATR)',
                    },
                    {
                        internal_name: TechnicalType.AVERAGE_VOLUME,
                        label: 'Average Volume',
                    },
                    {
                        internal_name: TechnicalType.CMF,
                        label: 'Chaikin Money Flow (CMF)',
                    },
                    {
                        internal_name: TechnicalType.CCI,
                        label: 'Commodity Channel Index (CCI)',
                    },
                    {
                        internal_name: TechnicalType.DMI,
                        label: 'Directional Movement Index (DMI)',
                    },
                    {
                        internal_name: TechnicalType.MACD,
                        label: 'Moving Average Conv/Div (MACD)',
                    },
                    {
                        internal_name: TechnicalType.MA_OSCILLATOR,
                        label: 'Moving Average Oscillator',
                    },
                    {
                        internal_name: TechnicalType.OBV,
                        label: 'On-Balance Volume',
                    },
                    {
                        internal_name: TechnicalType.ROC,
                        label: 'Rate of Change (ROC)',
                    },
                    {
                        internal_name: TechnicalType.ROLLING_VOL,
                        label: 'Rolling Volatility',
                    },
                    {
                        internal_name: TechnicalType.RSI,
                        label: 'Relative Strength Index (RSI)',
                    },
                    {
                        internal_name: TechnicalType.STOCHASTIC_FAST,
                        label: 'Fast Stochastic',
                    },
                    {
                        internal_name: TechnicalType.STOCHASTIC_FULL,
                        label: 'Full Stochastic',
                    },
                    {
                        internal_name: TechnicalType.STOCHASTIC_SLOW,
                        label: 'Slow Stochastic',
                    },
                    {
                        internal_name: TechnicalType.WILLIAMS_R,
                        label: 'Williams %R',
                    },
                    {
                        internal_name: TechnicalType.MOMENTUM_OSCILLATOR,
                        label: 'Momentum Oscillator',
                    },
                    {
                        internal_name: TechnicalType.STOCHASTIC_RSI,
                        label: 'Stochastic Relative Strength Index',
                    },
                    {
                        internal_name: TechnicalType.TRIX,
                        label: 'TRIX',
                    },
                ],
                section_label: '',
            },
        ],
    },
    /**
     * Volume is neither a oscillator or overlay. Volume is the total quantity of shares or
     * contracts traded for a specific security. For more information
     * please visit: https://www.investopedia.com/terms/v/volumeoftrade.asp
     */
    {
        cat_section: 'pro_metrics',
        category_label: 'Other',
        sections: [
            {
                items: [
                    {
                        internal_name: 'volume',
                        label: 'Volume',
                    },
                ],
                section_label: '',
            },
        ],
    },
];
export const DEFAULT_CROSSHAIR_OPTIONS = {
    color: '#808080',
    dashStyle: 'Solid',
    snap: false,
    width: 0,
};
export const TECH_CHART_ANNOTATION_KEY_TO_DISPLAY_NAME = {
    label: TechChartAnnotationName.TEXT,
    circle: TechChartAnnotationName.CIRCLE,
    rectangle: TechChartAnnotationName.RECTANGLE,
    segment: TechChartAnnotationName.TREND_SEGMENT,
    arrowSegment: TechChartAnnotationName.ARROW_SEGMENT,
    ray: TechChartAnnotationName.RAY,
    arrowRay: TechChartAnnotationName.ARROW_RAY,
    infinityLine: TechChartAnnotationName.LINE,
    arrowInfinityLine: TechChartAnnotationName.ARROW_LINE,
    horizontalLine: TechChartAnnotationName.HORIZONTAL_LINE,
    verticalLine: TechChartAnnotationName.VERTICAL_LINE,
    verticalLabel: TechChartAnnotationName.LABEL_COUNTER,
    verticalCounter: TechChartAnnotationName.VERTICAL_COUNTER,
    verticalArrow: TechChartAnnotationName.ARROW_COUNTER,
    crooked3: TechChartAnnotationName.CROOKED_LINE_3,
    crooked5: TechChartAnnotationName.CROOKED_LINE_5,
    elliott3: TechChartAnnotationName.ELLIOTT_3,
    elliott5: TechChartAnnotationName.ELLIOTT_5,
    measurexy: TechChartAnnotationName.MEASURE_XY,
    measurex: TechChartAnnotationName.MEASURE_X,
    measurey: TechChartAnnotationName.MEASURE_Y,
    pitchfork: TechChartAnnotationName.PITCHFORK,
    parallelChannel: TechChartAnnotationName.PARALLEL_CHANNEL,
    fibonacci: TechChartAnnotationName.FIBONACCI,
};
export const DEFAULT_ANNOTATION_FONT_SIZE = '11';
export const DEFAULT_ANNOTATION_TEXT_COLOR = '#ffffff';
export const DEFAULT_ANNOTATION_FILL_COLOR = '#000000bf';
export const DEFAULT_ANNOTATION_LINE_WEIGHT = '1';
export const DEFAULT_ANNOTATION_LINE_COLOR = '#000000bf';
